import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from './LoginForm.module.scss';
import classes from 'classnames';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import googleLogo from '@/assets/svg/google-login.svg';
import config from '@/config';
import googleTokenExchange from '@/services/tokenExchange';

import { InitialLoginProps, LoginValues } from '@/types/login';
import AppInput from '../AppInput/AppInput';
import TheButton from '../TheButton/TheButton';
import AppLoaderCircle from '../AppLoaderCircle';
import { authActions, authSelector } from '@/store/reducers/auth';
import { gameActions } from '@/store/reducers/game';
import { useAppDispatch, useAppSelector } from '@/store';
import commonUtils from '@/utils/common';
import userUtils from '@/utils/user';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

type FormSubmitValues = {
  email: string;
  password: string;
};

declare global {
  interface Window {
    google: any;
  }
}

const InitialLogin = ({
  emitSubmit,
  goToResetPassword,
  loading,
  styles,
  setLoginError,
}: InitialLoginProps) => {
  const { t } = useTranslation();
  const [showInputErrors, setShowInputErrors] = useState<boolean>(false);
  const [providerLoading, setProviderLoading] = useState<boolean>(false);
  const { getUserInfoError } = useAppSelector(authSelector);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const errorModal = useSelector((state: any) => state.ui);

  const showGoogleLogin = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const provider = params.get('provider');
    return config.googleLoginEnabled === 'true' || provider === 'google';
  }, [location.search, config.googleLoginEnabled]);

  const googleLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setProviderLoading(true);
      userUtils.openGoogleLogin();
    } catch (error) {
      console.error('Google login failed:', error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
    } finally {
      setProviderLoading(false);
    }
  };

  useEffect(() => {
    if (!getUserInfoError && !errorModal.showErrorModal && errorModal.silent && setLoginError) {
      setLoginError('Login failed');
    }
  }, [getUserInfoError, errorModal]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const languageCode = searchParams.get('language');
    if (languageCode) {
      const interfaceLanguage =
        languageCode && commonUtils.isSupportedLanguage(languageCode) ? languageCode : 'en';
      dispatch(authActions.setInterfaceLanguage(interfaceLanguage));
    }
  }, [window.location.href]);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required(t('validation.email_required').toString()),
    password: Yup.string().required(t('validation.password_required').toString()),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: (values: FormSubmitValues, formikHelpers: FormikHelpers<FormSubmitValues>) => {
      if (emitSubmit) {
        emitSubmit(values, formikHelpers as FormikHelpers<LoginValues>);
      }
    },
  });

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowInputErrors(true);
    formik.handleSubmit(event);
  };

  const onClear = (event: React.MouseEvent<SVGElement, MouseEvent>, name: string) => {
    formik.setFieldValue(name, '');
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    formik.setFieldValue(
      name,
      value
        .replace(/\s/g, '')
        .replace(/\u202B|\u202C/g, '')
        .split('')
        .join('')
    );
  };

  const handleQueryParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      code: params.get('code'),
      reconnect: params.get('reconnect'),
      accessToken: params.get('access_token'),
      error: params.get('error'),
      errorDescription: params.get('error_description'),
    };
  }, []);

  useEffect(() => {
    const { code, reconnect, error, errorDescription } = handleQueryParams();

    if (code) {
      setProviderLoading(true);
      (async () => {
        try {
          dispatch(gameActions.clearAll());
          const authData = await googleTokenExchange(code, dispatch);
          dispatch(authActions.signIn(authData));
        } catch (error) {
          console.error(error);
          if (setLoginError) {
            setLoginError('Login failed');
          }
        }
      })();
    } else if (reconnect) {
      userUtils.openGoogleLogin();
    } else if (error) {
      setProviderLoading(false);

      if (
        error === 'invalid_request' &&
        errorDescription?.includes('Unauthorized') &&
        setLoginError
      ) {
        setLoginError(`Unauthorized`);
      } else if (
        error === 'invalid_request' &&
        errorDescription?.includes('PreSignUp failed') &&
        setLoginError
      ) {
        setLoginError(`Unauthorized`);
      } else if (
        error === 'invalid_request' &&
        errorDescription?.includes('User is not enabled') &&
        setLoginError
      ) {
        setLoginError(`Login failed: ${errorDescription || error}`);
      } else if (setLoginError) {
        setLoginError('An unexpected error occurred during login.');
      }
    }
  }, [dispatch, handleQueryParams]);

  if (config.isMGWebsite) {
    return (
      <div className={classNames.formWrapper}>
        {loading || providerLoading ? (
          <div className={styles.loadingWrapper}>
            <AppLoaderCircle />
            <div className='mt-3'>{t('signYouIn')}</div>
          </div>
        ) : (
          <button type='button' className={classNames.googleButtonMGsite} onClick={googleLogin}>
            <img src={googleLogo} alt='Google' className={classNames.googleLogo} />
            <span>{t('mgWebsite.loginFormButton')}</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={classNames.formWrapper}>
      {!loading && !providerLoading && (
        <>
          <form onSubmit={onFormSubmit}>
            <AppInput
              className={styles.formField}
              inputWrapperClassName={classes(styles.appInputWrapper, {
                [styles.appInputWrapperError]: showInputErrors && !!formik.errors.email,
              })}
              id='email'
              name='email'
              label={t('emailOrUsername') ?? ''}
              placeholder={t('emailOrUsername') ?? ''}
              value={formik.values?.email?.trim()}
              error={showInputErrors ? formik.errors.email : ''}
              allowClear={true}
              emitChange={handleChange}
              emitBlur={formik.handleBlur}
              emitClear={onClear}
            />
            <AppInput
              className={styles.formField}
              inputWrapperClassName={classes(styles.appInputWrapper, {
                [styles.appInputWrapperError]: showInputErrors && !!formik.errors.password,
              })}
              id='password'
              name='password'
              type='password'
              label={t('password') ?? ''}
              placeholder={t('password') ?? ''}
              value={formik.values?.password?.trim()}
              error={showInputErrors ? formik.errors.password : ''}
              allowClear={true}
              emitChange={handleChange}
              emitBlur={formik.handleBlur}
              emitClear={onClear}
            />

            {!loading && !providerLoading && (
              <>
                <TheButton
                  type='submit'
                  className={classes(styles.submit, {
                    [styles.submitUp]: showGoogleLogin,
                  })}
                  text={t('signIn')}
                  shouldRecolor={false}
                />
                <button
                  className={classes('text-center cursor-pointer', styles.forgotPasswordBtn)}
                  onClick={goToResetPassword}
                >
                  {t('forgotPassword')}
                </button>
              </>
            )}
          </form>

          {showGoogleLogin && (
            <div className={classNames.googleLoginWrapper}>
              <div className={classNames.header}>
                <hr className={classNames.line} />
                <span className={classNames.text}>{t('orLoginWithGoogle')}</span>
                <hr className={classNames.line} />
              </div>
              <button type='button' className={classNames.googleButton} onClick={googleLogin}>
                <img src={googleLogo} alt='Google' className={classNames.googleLogo} />
              </button>
            </div>
          )}
        </>
      )}
      {(loading || providerLoading) && (
        <div className={styles.loadingWrapper}>
          <AppLoaderCircle />
          <div className='mt-3'>{t('signYouIn')}</div>
        </div>
      )}
    </div>
  );
};

export default InitialLogin;
